import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Play, X } from 'lucide-react';
import { apiClient } from '../../api';
import Drawer from '../UI/Drawer';
import { errMessage, primaryBtn, primaryBtnStyle } from '../UI/ink';
import { FIELD, SEV_LABEL, SEV_TOKEN, leafSentence, lastFired } from './model';

const SAMPLE = JSON.stringify({
  input: {
    connections: [{ ip: '203.0.113.45', port: 443 }],
    dns: ['example.com'],
    ports: [{ port: 3389, protocol: 'tcp', service: 'rdp' }],
    system: { hostname: 'web-prod-01', os: 'Ubuntu 22.04' },
  },
  context: {},
}, null, 2);

// Run a saved rule against an asset's latest real data, or pasted JSON.
// The Worker evaluates it read-only: no finding, alert or observation is written.
export default function TestDrawer({ open, rule, onClose }) {
  const [mode, setMode] = useState('asset');
  const [hosts, setHosts] = useState([]);
  const [serverId, setServerId] = useState('');
  const [raw, setRaw] = useState(SAMPLE);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRes(null); setError(null);
    apiClient.get('/api/servers').then(r => {
      const list = (r.data?.servers || []).filter(s => s.source !== 'arp_passive' && !['DISCOVERED', 'ARCHIVED', 'IGNORED'].includes(s.lifecycle_state))
        .sort((a, b) => String(b.last_seen || '').localeCompare(String(a.last_seen || '')));
      setHosts(list);
      setServerId(id => id || list[0]?.id || '');
    }).catch(() => setHosts([]));
  }, [open]);
  useEffect(() => { setRes(null); }, [mode, serverId]);
  if (!open || !rule) return null;

  const run = async () => {
    setBusy(true); setError(null); setRes(null);
    try {
      let body;
      if (mode === 'asset') body = { server_id: serverId };
      else {
        let parsed;
        try { parsed = JSON.parse(raw); } catch { throw new Error('That isn’t valid JSON.'); }
        body = parsed && parsed.input ? parsed : { input: parsed };
      }
      const r = await apiClient.post(`/api/rules/${rule.id}/test`, body);
      setRes(r.data);
    } catch (err) {
      setError(err.message && !err.response ? err.message : errMessage(err, 'The test could not run.'));
    } finally {
      setBusy(false);
    }
  };

  const verdict = !res ? null
    : res.fires ? { t: 'WOULD FIRE', tok: 'high' }
    : res.matched ? { t: 'MATCHED, BUT EXCEPTED', tok: 'low' }
    : { t: 'WOULD NOT FIRE', tok: 'good' };
  const src = res?.source;
  const sevTok = SEV_TOKEN[rule.severity] || 'low';

  return (
    <Drawer open={open} onClose={onClose} kicker="TEST RULE" title={rule.name} subtitle="Testing never creates findings or alerts.">
      <div className="flex flex-col gap-[18px]">
        <div role="group" aria-label="What to test against" className="flex p-[3px] gap-0.5 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
          {[['asset', 'An asset’s latest data'], ['json', 'Paste JSON']].map(([id, t]) => {
            const on = mode === id;
            return <button key={id} type="button" aria-pressed={on} onClick={() => setMode(id)} className="flex-1 h-[38px] px-3 rounded-[7px] text-[13px]" style={{ background: on ? 'var(--surf-raised)' : 'transparent', color: on ? 'var(--ink-text)' : 'var(--ink-muted)', fontWeight: on ? 600 : 400 }}>{t}</button>;
          })}
        </div>

        {mode === 'asset' ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="t-asset" className="text-[13px] font-medium">Asset</label>
            <select id="t-asset" value={serverId} onChange={e => setServerId(e.target.value)} className="h-[42px] box-border px-2.5 rounded-[9px] border text-[13.5px]" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}>
              {!hosts.length && <option value="">No registered assets</option>}
              {hosts.map(h => <option key={h.id} value={h.id}>{h.hostname}{h.last_seen ? ` · last seen ${lastFired(h.last_seen).toLowerCase()}` : ' · never checked in'}</option>)}
            </select>
            <span className="text-xs leading-snug" style={{ color: 'var(--ink-faint)' }}>Uses its last heartbeat (connections, DNS, programs) and last snapshot (ports, packages, services). History checks read the real history.</span>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="t-json" className="text-[13px] font-medium">Snapshot JSON</label>
            <textarea id="t-json" rows={10} value={raw} onChange={e => setRaw(e.target.value)} spellCheck={false} className="box-border px-3 py-2.5 rounded-[9px] border font-mono text-xs leading-relaxed resize-y outline-none" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }} />
            <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Same shape the agent sends: connections, dns, process_hashes, ports, packages, services, plus system for the host. context can carry threatIntels or previousServices.</span>
          </div>
        )}

        <button type="button" onClick={run} disabled={busy || (mode === 'asset' && !serverId)} className={`${primaryBtn} self-start`} style={primaryBtnStyle}><Play className="w-3.5 h-3.5" fill="currentColor" />{busy ? 'Running…' : 'Run test'}</button>
        {error && <div role="alert" className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>{error}</div>}

        {res && (
          <section aria-label="Test result" className="flex flex-col gap-3.5 p-4 rounded-xl border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
            <span className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-md font-mono text-[10.5px] font-medium tracking-[0.06em]" style={{ background: `var(--sev-${verdict.tok}-bg)` }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: `var(--sev-${verdict.tok}-fg)` }} />{verdict.t}</span>
              <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>{res.fires ? `${res.findings.length} finding${res.findings.length === 1 ? '' : 's'} · ` : ''}{res.duration_ms} ms{src?.hostname ? ` · ${src.hostname}` : ''}</span>
            </span>
            {src?.kind === 'asset' && (
              <span className="text-xs leading-snug" style={{ color: 'var(--ink-faint)' }}>
                Heartbeat {src.heartbeat_at ? lastFired(src.heartbeat_at).toLowerCase() : 'none yet'} · snapshot {src.snapshot_at ? lastFired(src.snapshot_at).toLowerCase() : 'none yet'}
                {res.input ? ` · ${res.input.connections} connections, ${res.input.dns} DNS lookups, ${res.input.ports} ports, ${res.input.packages} packages, ${res.input.services} services` : ''}
              </span>
            )}

            <div className="flex flex-col gap-1.5">
              <h3 className="m-0 font-mono text-[10.5px] font-medium tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>WHY</h3>
              {res.checks.map((c, i) => (
                <div key={i} className="grid grid-cols-[22px_minmax(0,1fr)] gap-2 py-2 border-b last:border-b-0" style={{ borderColor: 'var(--surf-line)' }}>
                  {c.passed ? <Check className="w-[18px] h-[18px]" strokeWidth={2.2} style={{ color: 'var(--sev-good-fg)' }} aria-label="Passed" /> : <X className="w-[18px] h-[18px]" strokeWidth={2.2} style={{ color: 'var(--sev-crit-fg)' }} aria-label="Did not pass" />}
                  <span className="flex flex-col gap-[3px] min-w-0">
                    <span className="text-[13px]">{leafSentence(c)}</span>
                    <span className="text-[12.5px] break-words" style={{ color: 'var(--ink-muted)' }}>{c.got}</span>
                  </span>
                </div>
              ))}
              {res.suppressed_by?.length > 0 && (
                <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>
                  Matched, but {res.suppressed_by.map(e => `the exception “${e.field === 'entity' ? 'matched value' : FIELD[e.field]?.label || e.field} ${e.operator.replace('_', ' ')} ${e.value}”`).join(' and ')} {res.fires ? 'dropped some values' : 'stopped it'}.
                </span>
              )}
            </div>

            {res.fires && (
              <div className="flex flex-col gap-1.5">
                <h3 className="m-0 font-mono text-[10.5px] font-medium tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>THE FINDING{res.findings.length === 1 ? '' : 'S'} IT WOULD RAISE</h3>
                <div className="flex flex-col gap-1.5 px-3.5 py-3 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
                  <span className="flex items-center gap-2"><span className="inline-flex items-center gap-[5px] h-[22px] px-[7px] rounded-md font-mono text-[10px] font-medium" style={{ background: `var(--sev-${sevTok}-bg)` }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: `var(--sev-${sevTok}-fg)` }} />{SEV_LABEL[rule.severity]}</span><span className="text-[13.5px] font-semibold">{rule.name}</span></span>
                  {[['Asset', src?.hostname || 'pasted data', false], [res.findings.length === 1 ? 'Entity' : 'Entities', res.entities.join(', '), true], ['ATT&CK', (rule.mitre_techniques || []).join(', ') || '—', true]].map(([k, v, m]) => (
                    <span key={k} className="grid grid-cols-[84px_minmax(0,1fr)] gap-2 text-[12.5px] leading-relaxed"><span style={{ color: 'var(--ink-faint)' }}>{k}</span><span className={`break-words ${m ? 'font-mono' : ''}`}>{v}</span></span>
                  ))}
                  {res.existing?.map(e => (
                    <span key={e.finding_id} className="text-xs leading-snug" style={{ color: 'var(--ink-faint)' }}>
                      {e.entity} is already open as <Link to={`/findings?finding=${e.finding_id}`} style={{ color: 'var(--ink-link)' }}>{e.finding_id}</Link>
                      {e.case_id ? <> in <Link to={`/cases/${e.case_id}`} style={{ color: 'var(--ink-link)' }}>{String(e.case_id).toUpperCase()}</Link></> : null}, so the real engine wouldn’t add another.
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </Drawer>
  );
}
